/**
 * The KPI strip. Two rows, no prose.
 *
 * Row one is stock health and every tile is a filter: click Critical and the table below shows
 * exactly the SKUs that tile counted. Row two is money and volume, each against the prior 30
 * days, with a sparkline on the two that have a series.
 *
 * No tile computes anything. Every number is a field on `StockPageResponse`, and the delta is
 * the server's current figure against the server's prior figure. The definition of each tile
 * lives in the hover popover and on /legend, nowhere else on this page.
 *
 * A packer's response carries none of the money fields (CONTRACTS.md 3.6), and for her the
 * money tiles are not rendered: Revenue at risk leaves row one, and row two is the 30-day
 * units tile alone. Absent data is an absent tile, never a tile reading "no data".
 */
import type { StockPageWindow } from '@elutide/shared';
import { KpiTile, Sparkline } from '@/components';
import { formatMoney, formatPercent, shortDate } from '@/lib/format';
import { TILE_LEGEND_ID, definitionFor, type LegendIndex } from './legend';
import { kpisHaveMoney, type AnyStockPageKpis } from './types';

export type StockStatusFilter = 'all' | 'critical' | 'low' | 'out';

export interface StockKpiStripProps {
  kpis: AnyStockPageKpis;
  window: StockPageWindow;
  legend: LegendIndex;
  filter: StockStatusFilter;
  onFilter: (next: StockStatusFilter) => void;
}

/**
 * Delta text against the prior window.
 *
 * A prior of zero has no percentage, so it says "no prior 30d" rather than dividing by zero
 * and printing infinity, which is the failure mode this whole screen was rebuilt to stop.
 */
function delta(
  current: number | null,
  prior: number | null,
  format: (value: number) => string,
): { text: string; tone?: 'ok' | 'warn' | 'crit' } | null {
  if (current === null || prior === null) return null;
  const change = current - prior;
  if (prior === 0) {
    return change === 0
      ? { text: 'flat, no prior 30d' }
      : { text: `${format(change)} vs no prior 30d`, tone: change > 0 ? 'ok' : 'warn' };
  }
  const pct = change / Math.abs(prior);
  const sign = change > 0 ? '+' : '';
  return {
    text: `${sign}${format(change)} vs prior 30d, ${sign}${(pct * 100).toFixed(1)}%`,
    tone: change === 0 ? undefined : change > 0 ? 'ok' : 'warn',
  };
}

/** A margin delta is in points, not in percent of a percent. */
function marginDelta(
  current: number | null,
  prior: number | null,
): { text: string; tone?: 'ok' | 'warn' | 'crit' } | null {
  if (current === null || prior === null) return null;
  const points = (current - prior) * 100;
  const sign = points > 0 ? '+' : '';
  return {
    text: `${sign}${points.toFixed(1)} pts vs prior 30d`,
    tone: points === 0 ? undefined : points > 0 ? 'ok' : 'warn',
  };
}

const money = (cents: number) => formatMoney(Math.abs(cents), { nullText: 'no data' });
const units = (value: number) => `${Math.abs(value)} units`;

export function StockKpiStrip({ kpis, window, legend, filter, onFilter }: StockKpiStripProps) {
  const def = (id: string) => definitionFor(legend, id);
  const toggle = (next: StockStatusFilter) => onFilter(filter === next ? 'all' : next);
  const withMoney = kpisHaveMoney(kpis);

  return (
    <div className="stock-kpis">
      <div className="stock-kpis__row" role="group" aria-label="Stock health">
        <KpiTile
          label="Critical"
          value={kpis.critical}
          tone="crit"
          showProvenance={false}
          definition={def(TILE_LEGEND_ID.critical)}
          pressed={filter === 'critical'}
          onClick={() => toggle('critical')}
        />
        <KpiTile
          label="Low"
          value={kpis.low}
          tone="warn"
          showProvenance={false}
          definition={def(TILE_LEGEND_ID.low)}
          pressed={filter === 'low'}
          onClick={() => toggle('low')}
        />
        <KpiTile
          label="Out of stock"
          value={kpis.out}
          showProvenance={false}
          definition={def(TILE_LEGEND_ID.out)}
          pressed={filter === 'out'}
          onClick={() => toggle('out')}
        />
        {withMoney ? (
          <KpiTile
            label="Revenue at risk"
            value={kpis.revenue_at_risk_cents}
            unit="cents"
            showProvenance={false}
            definition={def(TILE_LEGEND_ID.revenueAtRisk)}
            note={`${kpis.revenue_at_risk_skus} SKUs under 14 days`}
            pressed={filter === 'critical'}
            onClick={() => toggle('critical')}
          />
        ) : null}
      </div>

      <div
        className="stock-kpis__row"
        role="group"
        aria-label={withMoney ? `Money, ${window.label}` : `Volume, ${window.label}`}
      >
        {withMoney ? (
          <>
            <KpiTile
              label="Inventory at cost"
              value={kpis.inventory_at_cost_cents}
              unit="cents"
              showProvenance={false}
              definition={def(TILE_LEGEND_ID.inventoryAtCost)}
            />
            <KpiTile
              label="Inventory at retail"
              value={kpis.inventory_at_retail_cents}
              unit="cents"
              showProvenance={false}
              definition={def(TILE_LEGEND_ID.inventoryAtRetail)}
            />
            <KpiTile
              label="Inv value"
              value={kpis.inv_value_cents}
              unit="cents"
              showProvenance={false}
              definition={def(TILE_LEGEND_ID.invValue)}
            />
            <KpiTile
              label="Open PO value"
              value={kpis.open_po_value_cents}
              unit="cents"
              showProvenance={false}
              definition={def(TILE_LEGEND_ID.openPoValue)}
              note={kpis.next_po_eta ? `next ETA ${shortDate(kpis.next_po_eta)}` : 'no open POs'}
            />
          </>
        ) : null}
        <KpiTile
          label="30-day units"
          value={kpis.units_30d}
          unit="units"
          showProvenance={false}
          definition={def(TILE_LEGEND_ID.units30d)}
          delta={delta(kpis.units_30d, kpis.units_prior_30d, units)}
        >
          <Sparkline points={kpis.sparklines.units} label="30 day units by day" />
        </KpiTile>
        {withMoney ? (
          <>
            <KpiTile
              label="30-day revenue"
              value={kpis.revenue_30d_cents}
              unit="cents"
              showProvenance={false}
              definition={def(TILE_LEGEND_ID.revenue30d)}
              delta={delta(kpis.revenue_30d_cents, kpis.revenue_prior_30d_cents, money)}
            >
              <Sparkline
                points={kpis.sparklines.revenue_cents}
                tone="ok"
                label="30 day revenue by day"
              />
            </KpiTile>
            <KpiTile
              label="Catalog margin"
              value={kpis.margin_pct}
              unit="ratio"
              showProvenance={false}
              definition={def(TILE_LEGEND_ID.catalogMargin)}
              delta={marginDelta(kpis.margin_pct, kpis.margin_prior_pct)}
              note={
                kpis.margin_pct === null ? null : formatPercent(kpis.margin_prior_pct) + ' prior'
              }
            />
          </>
        ) : null}
      </div>
    </div>
  );
}
